import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Package, Clock, CheckCircle, XCircle, ChevronRight, Search, 
  ShoppingBag, RefreshCw, Filter, ArrowRight, ArrowLeft, Calendar
} from "lucide-react";
import api from "./axiosConfig";
import { useCart } from "./CartContext";
import { initClientSocket, getSocket } from "./socketClient";

export default function Commandes() {
  const navigate = useNavigate();
  const { clearCart, setCartFromOrder } = useCart(); 
  const phone = localStorage.getItem("customerPhone");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const fetchOrders = async (pageToLoad = page) => {
    try {
      const res = await api.get(`/orders/client/${phone}`, {
        params: { page: pageToLoad, limit, tab: activeTab, q: searchTerm },
      });
      const sortedOrders = (res.data.orders || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sortedOrders);
      setTotalPages(res.data.totalPages || 1);
      setPage(res.data.page || pageToLoad);
    } catch (err) {
      console.error("Erreur chargement commandes client", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!phone) { navigate("/login-client"); return; }
    fetchOrders(page);

    initClientSocket(phone); 
    const socket = getSocket();
    const handleOrderUpdate = () => fetchOrders(page); 

    socket.on("client_order_created", handleOrderUpdate);
    socket.on("client_order_updated", handleOrderUpdate);
    socket.on("client_order_cancelled", handleOrderUpdate);

    return () => {
      socket.off("client_order_created", handleOrderUpdate);
      socket.off("client_order_updated", handleOrderUpdate);
      socket.off("client_order_cancelled", handleOrderUpdate);
    };
  }, [phone, navigate, page, activeTab, searchTerm]);
  
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const activeStatuses = ["en_attente", "en_preparation"]; 
  const cancellableStatuses = ["en_attente", "en_preparation", "prete"];

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Voulez-vous vraiment annuler cette commande ?")) return;
    try {
      await api.put(`/orders/${orderId}/cancel`, { phone });
      fetchOrders(page);
    } catch (err) {
      alert(err?.response?.data?.error || "Impossible d’annuler.");
    }
  };
  
  const getStatusConfig = (status) => {
    switch (status) {
        case "en_attente":
            return { label: "En attente", color: "bg-slate-100 text-slate-600", accent: "bg-slate-400", icon: Clock };
        case "en_preparation":
            return { label: "En préparation", color: "bg-orange-100 text-orange-600", accent: "bg-orange-500", icon: Package };
        case "prete":
            return { label: "Prête", color: "bg-green-100 text-green-600", accent: "bg-green-500", icon: CheckCircle };
        case "livree":
            return { label: "Terminée", color: "bg-blue-100 text-blue-600", accent: "bg-blue-500", icon: CheckCircle };
        case "annulee":
        case "annulee_client":
            return { label: "Annulée", color: "bg-red-50 text-red-500", accent: "bg-red-400", icon: XCircle };
        default:
            return { label: status, color: "bg-gray-100 text-gray-600", accent: "bg-gray-400", icon: Package };
    }
  };

const filteredOrders = orders; // ✅ backend fait déjà le filtre   

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#FAFAFA]">
      <div className="w-12 h-12 border-4 border-[#f34141] border-t-transparent rounded-full animate-spin mb-4" />
      <p className="font-bold text-slate-400 animate-pulse">Synchronisation de vos commandes...</p>
    </div>
  );

  return (
    <div className="min-h-screen px-4 pb-32 font-sans bg-[#FAFAFA] pt-28">
      
      {/* HEADER PREMIUM */}
      <div className="max-w-3xl mx-auto mb-10">
        <h1 className="text-4xl font-black leading-tight tracking-tight text-slate-900">
          Mes <span className="text-[#f34141]">Commandes</span>
        </h1>
        <p className="mt-2 font-bold text-slate-400">Suivez l'état de vos achats en temps réel.</p>
      </div>

      <div className="max-w-3xl mx-auto">
        
        {/* BARRE DE RECHERCHE & FILTRE */}
        <div className="flex gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute w-5 h-5 -translate-y-1/2 text-slate-300 left-5 top-1/2" />
            <input 
              type="text"
              placeholder="Rechercher un N° ou un montant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full py-4 pr-6 font-bold transition-all bg-white border border-gray-100 shadow-sm pl-14 rounded-2xl text-slate-600 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-red-50"
            />
          </div>
          <button className="px-5 bg-white border border-gray-100 shadow-sm rounded-2xl text-slate-400 hover:text-[#f34141] transition-colors">
              <Filter size={20} />
          </button>
        </div>

        {/* TABS SWITCHER (SOFT UI) */}
        <div className="flex p-1.5 mb-10 bg-gray-100 rounded-[2rem] shadow-inner">
          <button
            onClick={() => setActiveTab("active")}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-black rounded-[1.7rem] transition-all duration-300 ${
              activeTab === "active" ? "bg-white text-slate-900 shadow-md" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <Clock size={18} />
            En cours
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-black rounded-[1.7rem] transition-all duration-300 ${
              activeTab === "history" ? "bg-white text-slate-900 shadow-md" : "text-slate-400 hover:text-slate-600"
            }`}
          >
            <Calendar size={18} />
            Historique
          </button>
        </div>

        {/* LISTE DES COMMANDES */}
        <div className="space-y-6">
          {filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-up">
              <div className="w-24 h-24 mb-6 bg-white border border-gray-50 rounded-[2.5rem] shadow-sm flex items-center justify-center">
                  <ShoppingBag size={40} className="text-slate-200" />
              </div>
              <h3 className="text-xl font-black text-slate-800">C'est bien calme ici...</h3>
              <p className="max-w-xs mx-auto mt-2 font-bold text-slate-400">
                {activeTab === "active" 
                  ? "Pas de commandes en route pour le moment." 
                  : "Votre historique est encore vierge."}
              </p>
              <button 
                  onClick={() => navigate("/")}
                  className="flex items-center gap-2 px-8 py-4 mt-8 font-black text-white transition-all shadow-xl bg-slate-900 rounded-2xl shadow-slate-200 hover:bg-slate-800 active:scale-95"
              >
                  Faire mon shopping
                  <ArrowRight size={18} />
              </button>
            </div>
          ) : (
            filteredOrders.map((order, idx) => {
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;
              const priceDisplay = order.totalPrice || order.total || 0;

              return (
                <div 
                  key={order._id} 
                  onClick={() => navigate(`/commandes/${order.orderNumber}`)}
                  className="group relative p-6 bg-white border border-gray-50 shadow-sm cursor-pointer rounded-[2.5rem] hover:shadow-xl hover:shadow-slate-100 transition-all duration-300 animate-fade-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className={`absolute left-0 top-1/2 -translate-y-1/2 h-12 w-1.5 rounded-r-full ${statusConfig.accent}`}></div>

                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${statusConfig.color} transition-transform group-hover:scale-110`}>
                            <StatusIcon size={24} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-900 group-hover:text-[#f34141] transition-colors tracking-tight">#{order.orderNumber}</h3>
                            <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Clock size={12} />
                                {new Date(order.createdAt).toLocaleDateString("fr-FR", { day: 'numeric', month: 'short' })} à {new Date(order.createdAt).toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        </div>
                    </div>
                    <div className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${statusConfig.color} border border-transparent group-hover:border-current transition-all`}>
                      {statusConfig.label}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-5 border-t border-gray-50">
                      <div className="flex flex-col">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 pr-4">Montant Total</span>
                          <span className="text-[#f34141] font-black text-2xl tracking-tighter pr-4">
                              {priceDisplay} <span className="ml-1 text-sm font-bold text-slate-300">MRU</span>
                          </span>
                      </div>

                      <div className="flex items-center gap-3">
                          {cancellableStatuses.includes(order.status) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCancelOrder(order._id); }}
                              className="px-5 py-2.5 text-xs font-black text-red-500 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
                            >
                              Annuler
                            </button>
                          )}

                          {activeTab === "history" && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await setCartFromOrder(order.items, order.merchantId);
                                  navigate("/panier");
                                }}
                                className="p-3 bg-slate-900 text-white rounded-xl hover:bg-[#f34141] transition-all shadow-md shadow-slate-100"
                                title="Commander à nouveau"
                              >
                                <RefreshCw size={18} />
                              </button>
                            )}
                          
                          <div className="flex items-center justify-center w-10 h-10 transition-all bg-slate-50 rounded-xl text-slate-300 group-hover:bg-slate-900 group-hover:text-white">
                              <ChevronRight size={20} />
                          </div>
                      </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* PAGINATION PREMIUM */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2 mt-12 animate-fade-in">
            <button
              onClick={() => { setPage(p => Math.max(1, p - 1)); scrollToTop(); }}
              disabled={page <= 1}
              className="flex items-center gap-2 px-6 py-3 font-black transition-all bg-white border border-gray-100 shadow-sm text-slate-600 rounded-2xl disabled:opacity-30 hover:shadow-md active:scale-95"
            >
              <ArrowLeft size={18} />
              Précédent
            </button>

            <div className="px-5 py-3 text-sm font-black text-white shadow-xl bg-slate-900 rounded-2xl shadow-slate-200">
              {page} <span className="mx-1 text-slate-400">/</span> {totalPages}
            </div>

            <button
              onClick={() => { setPage(p => Math.min(totalPages, p + 1)); scrollToTop(); }}
              disabled={page >= totalPages}
              className="flex items-center gap-2 px-6 py-3 font-black transition-all bg-white border border-gray-100 shadow-sm text-slate-600 rounded-2xl disabled:opacity-30 hover:shadow-md active:scale-95"
            >
              Suivant
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

